import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';

import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { routes } from './app.routes';

import { MatPaginatorIntl } from '@angular/material/paginator';
import { CalAngularModule, CalAngularService } from '@cvx/cal-angular';
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { OktaAuthModule } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { API_BASE_URL as COMPLIANCEHUB_API_BASE_URL } from './modules/compliancehub/api/compliancehub-client';
import { API_BASE_URL as SP_API_BASE_URL } from './modules/sp/api/sp-client';
import { API_BASE_URL as CDD_API_BASE_URL } from './modules/cdd/api/cdd-client';
import { API_BASE_URL as SDD_API_BASE_URL } from './modules/sdd/api/sdd-client';
import { API_BASE_URL as BQ_API_BASE_URL } from './modules/bq/api/bq-client';
import { API_BASE_URL as CIW_API_BASE_URL } from './modules/investigations/api/ciw-client';
import { environment } from '../environments/environment';
import { MatPaginatorI18nService } from './mat-table-paginator-intl';
import { AppInterceptor } from './shared/interceptors/app.interceptor';
import { ApplicationInsightsService } from './shared/services/application-insights.service';

// Cache-busts the i18n JSON requests per page load (Date.now(), evaluated once at
// bootstrap) so a stale browser-cached translation file is never served after a deploy -
// hitting the endpoint directly returned "200 (from disk cache)" with outdated keys otherwise.
const httpLoaderFactory: (http: HttpClient) => TranslateHttpLoader = (http: HttpClient) =>
  new TranslateHttpLoader(http, './i18n/', `.json?v=${Date.now()}`);

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './i18n/', '.json');
}

export function initializeAuth(calService: CalAngularService): () => Promise<void> {
  return () => new Promise<void>(resolve => {
    // Wait for MSAL's handleRedirectPromise() and initialize() to complete.
    // In MSAL v2.37+, getAllAccounts() returns empty until initialize() completes,
    // which can take multiple async cycles. We poll until accounts are available
    // or timeout after a reasonable wait (max 2 seconds).
    let attempts = 0;
    const maxAttempts = 20; // 20 * 100ms = 2 seconds max

    const checkAuth = () => {
      calService.isUserSignedIn().subscribe({
        next: (isSignedIn) => {
          // If signed in, we're done
          if (isSignedIn) {
            resolve();
          }
          // If not signed in and still have attempts left, retry
          else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(checkAuth, 100);
          }
          // Otherwise, give up and continue (user is not authenticated)
          else {
            resolve();
          }
        },
        error: () => {
          // On error, retry if we have attempts left
          if (attempts < maxAttempts) {
            attempts++;
            setTimeout(checkAuth, 100);
          } else {
            resolve();
          }
        }
      });
    };

    // Start checking after initial delay
    setTimeout(checkAuth, 100);
  });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideHttpClient(
      withInterceptorsFromDi()
    ),
    importProvidersFrom(
      [
        TranslateModule.forRoot({
          loader: {
            provide: TranslateLoader,
            useFactory: httpLoaderFactory,
            deps: [HttpClient],
          },
        }),
        CalAngularModule.forRoot(environment.calConfig),
        OktaAuthModule.forRoot({
          oktaAuth: new OktaAuth({
            clientId: environment.okta.clientId,
            issuer: environment.okta.issuer,
            redirectUri: environment.okta.redirectUri,
            scopes: environment.okta.scopes,
            pkce: environment.okta.pkce,
            postLogoutRedirectUri: environment.okta.postLogoutRedirectUri
          })
        })
      ]
    ),
    { provide: COMPLIANCEHUB_API_BASE_URL, useValue: environment.baseApiUrl },
    { provide: SP_API_BASE_URL, useValue: environment.baseApiUrl },
    { provide: CDD_API_BASE_URL, useValue: environment.baseApiUrl },
    { provide: SDD_API_BASE_URL, useValue: environment.baseApiUrl },
    { provide: BQ_API_BASE_URL, useValue: environment.baseApiUrl },
    { provide: CIW_API_BASE_URL, useValue: environment.baseApiUrl },
    { provide: HTTP_INTERCEPTORS, useClass: AppInterceptor, multi: true },
    { provide: MatPaginatorIntl, useClass: MatPaginatorI18nService },
    { provide: APP_INITIALIZER, useFactory: initializeAuth, deps: [CalAngularService], multi: true },
    ApplicationInsightsService
  ]
};
